import {
    type CalendarDate,
    type DateValue,
    endOfMonth,
    endOfYear,
    getLocalTimeZone,
    startOfMonth,
    startOfYear,
    today,
} from '@internationalized/date';

/**
 * Pre-computed shortcut buttons, in render order. Each one drives the
 * Segment on the left of `PeriodControl` plus the implied date range
 * shown in the DateRangePicker on the right. `'custom'` is the
 * fallback when the picker's range doesn't match any preset.
 */
export type PeriodShortcut =
    'today' | 'thisWeek' | 'thisMonth' | 'thisYear' | 'custom';

export const PERIOD_SHORTCUTS: ReadonlyArray<PeriodShortcut> = [
    'today',
    'thisWeek',
    'thisMonth',
    'thisYear',
    'custom',
];

/**
 * i18n key (relative to the `pages` namespace) for each shortcut. The
 * View pulls the localized label through this map so a label rename
 * is a single edit.
 */
export const PERIOD_SHORTCUT_LABEL_KEY: Record<PeriodShortcut, string> = {
    custom: 'sales.widgets.periodControl.custom',
    today: 'sales.widgets.periodControl.today',
    thisMonth: 'sales.widgets.periodControl.thisMonth',
    thisWeek: 'sales.widgets.periodControl.thisWeek',
    thisYear: 'sales.widgets.periodControl.thisYear',
};

/** ISO 8601 / Monday-first week. */
const MONDAY_DAY_INDEX = 1; // JS Date.getDay() — Mon = 1, Sun = 0.

/**
 * Returns the Monday of the week that `date` falls in, in the given
 * time zone. Manual implementation because `startOfWeek` from
 * `@internationalized/date` is locale-aware and we want Monday
 * unconditionally, regardless of the user's locale.
 */
export const startOfIsoWeek = (
    date: CalendarDate,
    tz: string,
): CalendarDate => {
    const jsDay = date.toDate(tz).getDay(); // Sun = 0, Mon = 1, ... Sat = 6
    const daysSinceMonday = (jsDay - MONDAY_DAY_INDEX + 7) % 7;
    return date.subtract({ days: daysSinceMonday });
};

/** Sunday of the ISO week `date` falls in (i.e. start-of-week + 6). */
export const endOfIsoWeek = (date: CalendarDate, tz: string): CalendarDate =>
    startOfIsoWeek(date, tz).add({ days: 6 });

export type PeriodRange = {
    start: DateValue;
    end: DateValue;
};

/**
 * Resolves a shortcut into a concrete date range, anchored to the
 * current day in the user's local time zone. Returns `null` for
 * `'custom'` — the caller keeps the existing range or shows an empty
 * picker.
 */
export const getRangeForShortcut = (
    shortcut: PeriodShortcut,
    tz: string = getLocalTimeZone(),
): PeriodRange | null => {
    if (shortcut === 'custom') {
        return null;
    }
    const t = today(tz);
    switch (shortcut) {
        case 'today':
            return { start: t, end: t };
        case 'thisWeek':
            return { start: startOfIsoWeek(t, tz), end: endOfIsoWeek(t, tz) };
        case 'thisMonth':
            return { start: startOfMonth(t), end: endOfMonth(t) };
        case 'thisYear':
            return { start: startOfYear(t), end: endOfYear(t) };
    }
};

/**
 * Returns the shortcut whose preset range matches `range` exactly,
 * or `'custom'` when none does. Used to drive the Segment's
 * `selectedKey` from the picker's current value — when the user
 * tweaks dates in the calendar the highlight automatically moves to
 * `'custom'`.
 */
export const detectShortcut = (
    range: PeriodRange | null,
    tz: string = getLocalTimeZone(),
): PeriodShortcut => {
    if (range === null) {
        return 'custom';
    }
    for (const shortcut of PERIOD_SHORTCUTS) {
        const preset = getRangeForShortcut(shortcut, tz);
        if (
            preset !== null &&
            range.start.compare(preset.start) === 0 &&
            range.end.compare(preset.end) === 0
        ) {
            return shortcut;
        }
    }
    return 'custom';
};
